import React from 'react';
import { formatCurrency, formatDate, formatCommissionRate, isFixedCommission } from '../../../lib/utils';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../../components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import {
  DollarSign, Check, X, CheckCircle2, AlertCircle, Edit3, Eye,
  FileText, User, Building, Calculator, Users, ArrowRight, History, Download,
} from 'lucide-react';
import { statusColors, statusLabels } from './claimConstants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function ClaimDetailsSheet({
  open, onOpenChange, claim, isPIC, isAdmin, isAccount, canApprove,
  onStartReview, onApprove, onProcessPayment, onRequestRevision, onReject, onLoadAuditTrail,
}) {
  if (!claim) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" /> Claim {claim.claim_ref}
          </SheetTitle>
          <SheetDescription>
            Submitted by {claim.agent_name} on {formatDate(claim.created_at)}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Status + Commission Statement download */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Badge className={`${statusColors[claim.status]} text-white px-3 py-1`}>
                {statusLabels[claim.status]}
              </Badge>
              {claim.revision_count > 0 && (
                <Badge variant="outline" className="text-orange-600">
                  {claim.revision_count} Revision(s)
                </Badge>
              )}
            </div>
            <a
              href={`${BACKEND_URL}/api/commission-claims/${claim.id}/commission-statement`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-commission-statement"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-1.5 rounded-md transition"
              title="Downloads the auto-generated PDF summary of this claim"
            >
              <Download className="h-3.5 w-3.5" />
              Commission Statement (PDF)
            </a>
          </div>

          {/* Customer Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="h-4 w-4" /> Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-gray-500">Full Name</p><p className="font-medium">{claim.customer_full_name}</p></div>
              <div><p className="text-gray-500">NRIC</p><p className="font-medium">{claim.customer_nric}</p></div>
              <div><p className="text-gray-500">Contact</p><p className="font-medium">{claim.customer_contact_no}</p></div>
              <div><p className="text-gray-500">Email</p><p className="font-medium">{claim.customer_email}</p></div>
            </CardContent>
          </Card>

          {/* Transaction Details */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building className="h-4 w-4" /> Transaction Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-gray-500">Project</p><p className="font-medium">{claim.project_name}</p></div>
              <div><p className="text-gray-500">Unit</p><p className="font-medium">{claim.unit_number}</p></div>
              <div><p className="text-gray-500">SPA Price</p><p className="font-medium">{formatCurrency(claim.spa_price)}</p></div>
              <div><p className="text-gray-500">Nett Price</p><p className="font-medium">{formatCurrency(claim.nett_price)}</p></div>
              <div><p className="text-gray-500">Booking Date</p><p className="font-medium">{claim.booking_date}</p></div>
              <div><p className="text-gray-500">Signing Date</p><p className="font-medium">{claim.signing_date}</p></div>
              <div><p className="text-gray-500">Bank</p><p className="font-medium">{claim.bank_name}</p></div>
              <div><p className="text-gray-500">Loan Amount</p><p className="font-medium">{formatCurrency(claim.loan_amount)}</p></div>
              <div><p className="text-gray-500">{isFixedCommission(claim) ? 'Commission (Fixed)' : 'Commission %'}</p><p className="font-medium">{formatCommissionRate(claim)}</p></div>
              <div><p className="text-gray-500">Lot Status</p><p className="font-medium capitalize">{claim.lot_status}</p></div>
            </CardContent>
          </Card>

          {/* SP SAM Pool - Agent Hierarchy */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="h-4 w-4" /> SP SAM Pool - Agent Commission
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rank</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead className="text-right">Rate %</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">SST (8%)</TableHead>
                    <TableHead className="text-right">Platform (5%)</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(claim.sp_sam_recipients?.length > 0 ? claim.sp_sam_recipients : claim.management_recipients || []).map((r, idx) => (
                    <TableRow key={`sp-${r.name || ''}-${idx}`}>
                      <TableCell className="font-medium">{r.rank_display}</TableCell>
                      <TableCell><div><p>{r.name}</p><p className="text-xs text-gray-500">{r.agent_code}</p></div></TableCell>
                      <TableCell className="text-right">{r.rate_percentage || '-'}%</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.gross_comm)}</TableCell>
                      <TableCell className="text-right text-red-600">-{formatCurrency(r.sst_amount)}</TableCell>
                      <TableCell className="text-right text-orange-600">-{formatCurrency(r.platform_fee)}</TableCell>
                      <TableCell className="text-right font-semibold text-green-600">{formatCurrency(r.balance_comm || r.nett_comm)}</TableCell>
                    </TableRow>
                  ))}
                  {/* SP SAM extras: PIC, Trip, Profit */}
                  {claim.pic_commission_amount > 0 && (
                    <TableRow>
                      <TableCell className="font-medium">PIC</TableCell>
                      <TableCell>{claim.pic_user_name || '-'}</TableCell>
                      <TableCell className="text-right">{claim.pic_rate || '-'}%</TableCell>
                      <TableCell className="text-right" colSpan={3}></TableCell>
                      <TableCell className="text-right font-semibold text-green-600">{formatCurrency(claim.pic_commission_amount)}</TableCell>
                    </TableRow>
                  )}
                  {claim.trip_wallet_amount > 0 && (
                    <TableRow>
                      <TableCell className="font-medium">Trip Wallet</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell className="text-right">{claim.trip_rate || '-'}%</TableCell>
                      <TableCell className="text-right" colSpan={3}></TableCell>
                      <TableCell className="text-right font-semibold text-teal-600">{formatCurrency(claim.trip_wallet_amount)}</TableCell>
                    </TableRow>
                  )}
                  {claim.sp_sam_profit_amount > 0 && (
                    <TableRow>
                      <TableCell className="font-medium">SP SAM Profit</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell className="text-right">{claim.sp_sam_profit_rate || '-'}%</TableCell>
                      <TableCell className="text-right" colSpan={3}></TableCell>
                      <TableCell className="text-right font-semibold text-blue-600">{formatCurrency(claim.sp_sam_profit_amount)}</TableCell>
                    </TableRow>
                  )}
                  <TableRow className="bg-gray-50">
                    <TableCell colSpan={3} className="font-semibold">SP SAM Pool Total</TableCell>
                    <TableCell className="text-right text-xs text-gray-500">Pool: {formatCurrency(claim.sp_sam_pool)}</TableCell>
                    <TableCell colSpan={2}></TableCell>
                    <TableCell className="text-right font-bold text-green-600">
                      {formatCurrency(
                        (claim.sp_sam_recipients?.length > 0 ? claim.sp_sam_recipients : claim.management_recipients || []).reduce((s, r) => s + (r.balance_comm || r.nett_comm || 0), 0)
                        + (claim.pic_commission_amount || 0) + (claim.trip_wallet_amount || 0) + (claim.sp_sam_profit_amount || 0)
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* SAM Rumah Pool - Co-Broke / Referral */}
          {claim.entity === 'sam_rumah' && (claim.sam_rumah_recipients?.length > 0 || claim.cobroke_recipients?.length > 0) && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ArrowRight className="h-4 w-4" /> SAM Rumah Pool - Co-Broke Commission
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Level</TableHead><TableHead>Name</TableHead>
                      <TableHead className="text-right">Rate %</TableHead><TableHead className="text-right">Gross</TableHead>
                      <TableHead className="text-right">SST (8%)</TableHead><TableHead className="text-right">Platform (5%)</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(claim.sam_rumah_recipients?.length > 0 ? claim.sam_rumah_recipients : claim.cobroke_recipients || []).map((r, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{r.rank_display || r.level_display}</TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell className="text-right">{r.rate_percentage || '-'}%</TableCell>
                        <TableCell className="text-right">{formatCurrency(r.gross_comm)}</TableCell>
                        <TableCell className="text-right text-red-600">-{formatCurrency(r.sst_amount)}</TableCell>
                        <TableCell className="text-right text-orange-600">-{formatCurrency(r.platform_fee)}</TableCell>
                        <TableCell className="text-right font-semibold text-purple-600">{formatCurrency(r.balance_comm || r.nett_comm)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-purple-50">
                      <TableCell colSpan={3} className="font-semibold">SAM Rumah Pool Total</TableCell>
                      <TableCell className="text-right text-xs text-gray-500">Pool: {formatCurrency(claim.sam_rumah_pool)}</TableCell>
                      <TableCell colSpan={2}></TableCell>
                      <TableCell className="text-right font-bold text-purple-600">
                        {formatCurrency(
                          (claim.sam_rumah_recipients?.length > 0 ? claim.sam_rumah_recipients : claim.cobroke_recipients || []).reduce((s, r) => s + (r.balance_comm || r.nett_comm || 0), 0)
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Pool Summary */}
          {claim.sam_rumah_pool > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center">
                <p className="text-xs text-gray-500">SAM Rumah Pool (30%)</p>
                <p className="font-bold text-purple-700">{formatCurrency(claim.sam_rumah_pool)}</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
                <p className="text-xs text-gray-500">SP SAM Pool (70%)</p>
                <p className="font-bold text-emerald-700">{formatCurrency(claim.sp_sam_pool)}</p>
              </div>
            </div>
          )}

          {/* Total */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Calculator className="h-5 w-5 text-blue-600" /><span className="font-semibold">Total Commission</span></div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-blue-600">{formatCurrency(claim.total_commission)}</span>
                  {claim.gross_commission > 0 && (
                    <p className="text-xs text-gray-500">Gross: {formatCurrency(claim.gross_commission)} | Net: {formatCurrency(claim.net_commission)}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documents */}
          {claim.documents && Object.keys(claim.documents).length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><FileText className="h-4 w-4" /> Supporting Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(claim.documents).map(([type, docs]) => (
                    <div key={type} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                      <FileText className="h-4 w-4 text-gray-400" />
                      <span className="text-sm capitalize">{type.replace(/_/g, ' ')}</span>
                      <Badge variant="outline">{Array.isArray(docs) ? docs.length : 1}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Revision History */}
          {claim.revision_requests && claim.revision_requests.length > 0 && (
            <Card className="border-orange-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2 text-orange-600">
                  <AlertCircle className="h-4 w-4" /> Revision History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {claim.revision_requests.map((rev, idx) => (
                    <div key={idx} className="p-3 bg-orange-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">{rev.requested_by_name}</span>
                        <span className="text-xs text-gray-500">{formatDate(rev.requested_at)}</span>
                      </div>
                      {rev.general_comment && <p className="text-sm text-gray-700">{rev.general_comment}</p>}
                      {rev.field_comments && Object.keys(rev.field_comments).length > 0 && (
                        <div className="mt-2 space-y-1">
                          {Object.entries(rev.field_comments).map(([field, comment]) => (
                            <div key={field} className="text-xs">
                              <span className="font-medium capitalize">{field.replace(/_/g, ' ')}:</span> <span className="text-gray-600">{comment}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 border-t flex-wrap">
            <Button variant="outline" onClick={() => onLoadAuditTrail(claim.id)}>
              <History className="h-4 w-4 mr-1" /> Audit Trail
            </Button>
            {claim.status === 'submitted' && isPIC && (
              <Button onClick={() => onStartReview(claim.id)}>Start Review</Button>
            )}
            {['submitted', 'under_review'].includes(claim.status) && isPIC && (
              <>
                <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={() => onApprove(claim.id)}><Check className="h-4 w-4 mr-1" /> PIC Approve</Button>
                <Button variant="outline" className="text-orange-600 border-orange-300" onClick={onRequestRevision}><Edit3 className="h-4 w-4 mr-1" /> Request Revision</Button>
                <Button variant="outline" className="text-red-600 border-red-300" onClick={() => { const r = prompt('Enter rejection reason:'); if (r) onReject(claim.id, r); }}><X className="h-4 w-4 mr-1" /> Reject</Button>
              </>
            )}
            {claim.status === 'pic_approved' && isAdmin && (
              <>
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => onApprove(claim.id)}><Check className="h-4 w-4 mr-1" /> Admin Approve</Button>
                <Button variant="outline" className="text-orange-600 border-orange-300" onClick={onRequestRevision}><Edit3 className="h-4 w-4 mr-1" /> Request Revision</Button>
                <Button variant="outline" className="text-red-600 border-red-300" onClick={() => { const r = prompt('Enter rejection reason:'); if (r) onReject(claim.id, r); }}><X className="h-4 w-4 mr-1" /> Reject</Button>
              </>
            )}
            {claim.status === 'admin_approved' && isAccount && (
              <>
                <Button className="bg-green-600 hover:bg-green-700" onClick={() => onApprove(claim.id)}><Check className="h-4 w-4 mr-1" /> Account Approve</Button>
                <Button variant="outline" className="text-orange-600 border-orange-300" onClick={onRequestRevision}><Edit3 className="h-4 w-4 mr-1" /> Request Revision</Button>
                <Button variant="outline" className="text-red-600 border-red-300" onClick={() => { const r = prompt('Enter rejection reason:'); if (r) onReject(claim.id, r); }}><X className="h-4 w-4 mr-1" /> Reject</Button>
              </>
            )}
            {claim.status === 'pic_approved' && isPIC && (
              <div className="flex items-center gap-2 text-cyan-600"><CheckCircle2 className="h-5 w-5" /><span className="text-sm">PIC Approved - Awaiting Admin approval</span></div>
            )}
            {claim.status === 'admin_approved' && (isPIC || isAdmin) && (
              <div className="flex items-center gap-2 text-indigo-600"><CheckCircle2 className="h-5 w-5" /><span className="text-sm">Admin Approved - Awaiting Account final approval</span></div>
            )}
            {claim.status === 'approved' && isAccount && (
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onProcessPayment(claim.id)}><DollarSign className="h-4 w-4 mr-1" /> Process Payment</Button>
            )}
            {claim.status === 'approved' && isPIC && (
              <div className="flex items-center gap-2 text-green-600"><CheckCircle2 className="h-5 w-5" /><span className="text-sm">Fully Approved - Awaiting Account payment processing</span></div>
            )}
            {claim.status === 'paid' && (
              <div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 className="h-5 w-5" /><span className="font-medium">Payment completed on {formatDate(claim.paid_at)}</span></div>
            )}
            {isAdmin && (
              <div className="flex items-center gap-2 text-gray-500 ml-auto"><Eye className="h-4 w-4" /><span className="text-sm">Admin view only</span></div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
